import * as Sentry from "@sentry/react";

// Public fallback DSN, safe to commit. A Sentry DSN ships in every
// browser bundle in clear text anyway (that's how the SDK knows where
// to send events), and Sentry's project-level spike protection handles
// any abuse of a leaked DSN. Inlining this guarantees the frontend
// reports errors even when the hosting platform (e.g. Lovable on an
// external-Supabase project) can't inject build-time env vars. If the
// hosting platform DOES inject VITE_SENTRY_DSN, that value wins.
//
// To disable Sentry in a specific environment (e.g. local dev), set
// VITE_SENTRY_DSN="" (empty string) in .env.local, the nullish-coalesce
// below keeps the empty string, and the early return skips init.
const PUBLIC_FALLBACK_DSN =
  "https://1e3f784cc008956bacb7e38b00346e25@o4511240830976000.ingest.de.sentry.io/4511240991277136";

/**
 * Initialise Sentry error tracking for the browser.
 *
 * Called once from main.tsx before React renders. PII is off by default;
 * if we ever need to attach user email or IP to events, flip sendDefaultPii
 * rather than sprinkling user data into individual capture calls.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN ?? PUBLIC_FALLBACK_DSN;
  if (!dsn) return;

  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT ||
    (import.meta.env.PROD ? "production" : "development");

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // 10% of transactions sampled for performance tracing.
    tracesSampleRate: 0.1,
    // No session replay by default; record on error only.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    // Known-noise filter list. Each entry is a substring or regex that
    // Sentry matches against the error message; matches are dropped before
    // they hit the dashboard. Reserve this list for errors we've
    // explicitly investigated and confirmed are unactionable, DON'T add
    // anything here just because it's noisy.
    ignoreErrors: [
      // Supabase JS uses navigator.locks for cross-tab session-refresh
      // coordination. When two tabs (or a link-preview bot opening
      // multiple URLs in quick succession in the same browser context)
      // race for the lock, the auth-js client uses the `steal` option
      // to forcibly take the lock, which causes the abandoned
      // promise to reject with `AbortError: Lock broken by another
      // request with the 'steal' option.` The error is benign per-event
      // (the second tab still completes its refresh successfully) and
      // not actionable from our code: it's library-internal flow
      // control. Confirmed source via session-replay review of a 22:33
      // UTC 2026-05-06 occurrence, Boydton-US Chrome bot crawling /,
      // /partner-apply, /order in 16s. Tracked in supabase/auth-js
      // upstream, no fix our side warranted.
      /Lock broken by another request with the 'steal' option/,
    ],
  });
}

export { Sentry };
